'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdAdd, MdBolt, MdDelete, MdInfoOutline, MdRefresh } from 'react-icons/md'
import { toast } from 'react-toastify'

import ConfirmModal from '@/components/confirm-modal'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    createDefaultMarketingEventMappings,
    createMarketingEventMapping,
    deleteMarketingEventMapping,
    getMarketingEventDispatches,
    getMarketingEventMappings,
    MARKETING_EVENT_DESTINATION_LABELS,
    MARKETING_EVENT_STATUS_COLORS,
    MARKETING_EVENT_STATUS_LABELS,
    MARKETING_EVENT_TRIGGER_HINTS,
    MARKETING_EVENT_TRIGGER_LABELS,
    MARKETING_EVENT_VALUE_SOURCE_LABELS,
    MarketingEventDestination,
    MarketingEventDispatch,
    MarketingEventMapping,
    MarketingEventTrigger,
    MarketingEventValueSource,
    META_STANDARD_EVENT_NAMES,
    updateMarketingEventMapping,
} from '@/lib/api/marketing/marketing-events-api'
import { getPipelines, PipelineStage, SalesPipeline } from '@/lib/api/sales/pipelines-api'

const TRIGGERS: MarketingEventTrigger[] = [
    'ORDER_SETTLED',
    'ORDER_FIRST_PAYMENT',
    'STAGE_ENTERED',
    'APPOINTMENT_SCHEDULED',
    'LEAD_CREATED',
    'APPOINTMENT_CANCELLED',
]

const VALUE_SOURCES: MarketingEventValueSource[] = [
    'NONE',
    'ORDER_TOTAL',
    'BILLING_PAID_TOTAL',
    'DEAL_VALUE',
]

interface FormState {
    trigger: MarketingEventTrigger
    pipelineId: string
    stageId: string
    destination: MarketingEventDestination
    eventName: string
    valueSource: MarketingEventValueSource
    active: boolean
}

const EMPTY_FORM: FormState = {
    trigger: 'ORDER_SETTLED',
    pipelineId: '',
    stageId: '',
    destination: 'META',
    eventName: 'Purchase',
    valueSource: 'ORDER_TOTAL',
    active: false,
}

const formatDateTime = (iso: string): string =>
    new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })

export default function MarketingEventsPage() {
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('marketing', 'events')

    const [mappings, setMappings] = useState<MarketingEventMapping[]>([])
    const [dispatches, setDispatches] = useState<MarketingEventDispatch[]>([])
    const [pipelines, setPipelines] = useState<SalesPipeline[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [showForm, setShowForm] = useState(false)
    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [toDelete, setToDelete] = useState<MarketingEventMapping | null>(null)
    const [deleting, setDeleting] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        const [mappingsRes, dispatchesRes] = await Promise.all([
            getMarketingEventMappings(),
            getMarketingEventDispatches({ limit: 30 }),
        ])
        setLoading(false)
        if (mappingsRes.error) {
            toast.error(mappingsRes.error)
            return
        }
        setMappings(mappingsRes.result ?? [])
        setDispatches(dispatchesRes.result ?? [])
    }, [])

    useEffect(() => {
        load()
        getPipelines().then(res => setPipelines(res.result ?? []))
    }, [load])

    const stagesOfPipeline = useMemo<PipelineStage[]>(() => {
        if (!form.pipelineId) return []
        const pipeline = pipelines.find(p => String(p.id) === form.pipelineId)
        return (pipeline?.stages ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder)
    }, [form.pipelineId, pipelines])

    const pipelineName = useCallback(
        (id: number | null) => pipelines.find(p => p.id === id)?.name ?? `#${id}`,
        [pipelines],
    )

    const stageName = useCallback(
        (pipelineId: number | null, stageId: number | null) => {
            const stage = pipelines
                .find(p => p.id === pipelineId)
                ?.stages?.find(s => s.id === stageId)
            return stage?.name ?? `#${stageId}`
        },
        [pipelines],
    )

    async function handleCreate() {
        if (!form.eventName.trim()) {
            toast.warn('Informe o nome do evento na plataforma.')
            return
        }
        setSaving(true)
        const res = await createMarketingEventMapping({
            trigger: form.trigger,
            pipelineId: form.trigger === 'STAGE_ENTERED' && form.pipelineId ? Number(form.pipelineId) : null,
            stageId: form.trigger === 'STAGE_ENTERED' && form.stageId ? Number(form.stageId) : null,
            destination: form.destination,
            eventName: form.eventName.trim(),
            valueSource: form.valueSource,
            active: form.active,
        })
        setSaving(false)
        if (res.error) {
            toast.error(res.error)
            return
        }
        toast.success('Evento mapeado.')
        setShowForm(false)
        setForm(EMPTY_FORM)
        await load()
    }

    async function handleToggle(mapping: MarketingEventMapping) {
        const res = await updateMarketingEventMapping(mapping.id, { active: !mapping.active })
        if (res.error) {
            toast.error(res.error)
            return
        }
        toast.success(mapping.active ? 'Evento desligado.' : 'Evento ligado.')
        await load()
    }

    async function handleDelete() {
        if (!toDelete) return
        setDeleting(true)
        const res = await deleteMarketingEventMapping(toDelete.id)
        setDeleting(false)
        setToDelete(null)
        if (res.error) {
            toast.error(res.error)
            return
        }
        toast.success('Mapeamento excluído.')
        await load()
    }

    async function handleDefaults() {
        const res = await createDefaultMarketingEventMappings()
        if (res.error) {
            toast.error(res.error)
            return
        }
        toast.success('Mapeamentos padrão criados.')
        await load()
    }

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                <div className="max-w-3xl">
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MdBolt /> Eventos
                    </h1>
                    <p className="text-base-content/60 text-sm mt-1">
                        O que o sistema avisa às plataformas de anúncio. O envio sai do servidor, então
                        o bloqueador de anúncio do navegador não o impede — mas a plataforma só casa o
                        evento com uma pessoa quando o lead tem identificador de clique, e-mail ou
                        telefone.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button className="btn btn-sm btn-ghost" onClick={load} title="Recarregar">
                        <MdRefresh className="text-base" />
                    </button>
                    {canCreate && mappings.length === 0 && (
                        <button className="btn btn-sm btn-outline" onClick={handleDefaults}>
                            Criar os padrão
                        </button>
                    )}
                    {canCreate && (
                        <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setShowForm(v => !v)}
                        >
                            <MdAdd className="text-base" /> Novo evento
                        </button>
                    )}
                </div>
            </div>

            {mappings.some(m => m.trigger === 'ORDER_FIRST_PAYMENT' && m.active) && (
                <div className="alert alert-warning text-sm py-3 mb-4 items-start">
                    <MdInfoOutline className="text-xl shrink-0" />
                    <span>
                        A venda é reportada já na entrada, com o valor cheio. Se o cliente
                        desistir depois, a plataforma não desconta sozinha: mapeie também o
                        cancelamento.
                    </span>
                </div>
            )}

            <div className="alert alert-info text-sm py-3 mb-4 items-start">
                <MdInfoOutline className="text-xl shrink-0" />
                <span>
                    Nenhum evento leva procedimento, especialidade ou qualquer dado de saúde: só o nome
                    do evento e o valor. É exigência das plataformas para anúncio de saúde.
                </span>
            </div>

            {showForm && canCreate && (
                <div className="card bg-base-100 border border-base-300 mb-6">
                    <div className="card-body gap-5">
                        <h2 className="font-semibold">Novo evento</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5 min-w-0">
                                <label className="text-sm font-medium" htmlFor="ev-trigger">
                                    Quando acontecer
                                </label>
                                <select
                                    id="ev-trigger"
                                    className="select select-bordered w-full"
                                    value={form.trigger}
                                    onChange={e =>
                                        setForm(f => ({
                                            ...f,
                                            trigger: e.target.value as MarketingEventTrigger,
                                            pipelineId: '',
                                            stageId: '',
                                        }))
                                    }
                                >
                                    {TRIGGERS.map(t => (
                                        <option key={t} value={t}>
                                            {MARKETING_EVENT_TRIGGER_LABELS[t]}
                                        </option>
                                    ))}
                                </select>
                                <span className="text-xs text-base-content/60">
                                    {MARKETING_EVENT_TRIGGER_HINTS[form.trigger]}
                                </span>
                            </div>

                            <div className="flex flex-col gap-1.5 min-w-0">
                                <label className="text-sm font-medium" htmlFor="ev-destination">
                                    Avisar
                                </label>
                                <select
                                    id="ev-destination"
                                    className="select select-bordered w-full"
                                    value={form.destination}
                                    onChange={e =>
                                        setForm(f => ({
                                            ...f,
                                            destination: e.target.value as MarketingEventDestination,
                                        }))
                                    }
                                >
                                    {(
                                        Object.keys(
                                            MARKETING_EVENT_DESTINATION_LABELS,
                                        ) as MarketingEventDestination[]
                                    ).map(d => (
                                        <option key={d} value={d} disabled={d !== 'META'}>
                                            {MARKETING_EVENT_DESTINATION_LABELS[d]}
                                            {d !== 'META' ? ' — em breve' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {form.trigger === 'STAGE_ENTERED' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1.5 min-w-0">
                                    <label className="text-sm font-medium" htmlFor="ev-pipeline">
                                        Funil
                                    </label>
                                    <select
                                        id="ev-pipeline"
                                        className="select select-bordered w-full"
                                        value={form.pipelineId}
                                        onChange={e =>
                                            setForm(f => ({
                                                ...f,
                                                pipelineId: e.target.value,
                                                stageId: '',
                                            }))
                                        }
                                    >
                                        <option value="">Qualquer funil</option>
                                        {pipelines.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1.5 min-w-0">
                                    <label className="text-sm font-medium" htmlFor="ev-stage">
                                        Etapa
                                    </label>
                                    <select
                                        id="ev-stage"
                                        className="select select-bordered w-full"
                                        value={form.stageId}
                                        onChange={e =>
                                            setForm(f => ({ ...f, stageId: e.target.value }))
                                        }
                                        disabled={!form.pipelineId}
                                    >
                                        <option value="">Qualquer etapa</option>
                                        {stagesOfPipeline.map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5 min-w-0">
                                <label className="text-sm font-medium" htmlFor="ev-name">
                                    Nome do evento na plataforma
                                </label>
                                <input
                                    id="ev-name"
                                    className="input input-bordered w-full"
                                    list="meta-event-names"
                                    value={form.eventName}
                                    maxLength={60}
                                    onChange={e => setForm(f => ({ ...f, eventName: e.target.value }))}
                                />
                                <datalist id="meta-event-names">
                                    {META_STANDARD_EVENT_NAMES.map(n => (
                                        <option key={n} value={n} />
                                    ))}
                                </datalist>
                                <span className="text-xs text-base-content/60">
                                    Os padrões da Meta aparecem na lista; um nome próprio também vale.
                                </span>
                            </div>

                            <div className="flex flex-col gap-1.5 min-w-0">
                                <label className="text-sm font-medium" htmlFor="ev-value">
                                    Valor do evento
                                </label>
                                <select
                                    id="ev-value"
                                    className="select select-bordered w-full"
                                    value={form.valueSource}
                                    onChange={e =>
                                        setForm(f => ({
                                            ...f,
                                            valueSource: e.target.value as MarketingEventValueSource,
                                        }))
                                    }
                                >
                                    {VALUE_SOURCES.map(v => (
                                        <option key={v} value={v}>
                                            {MARKETING_EVENT_VALUE_SOURCE_LABELS[v]}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <label className="flex items-center gap-2 cursor-pointer w-fit">
                            <input
                                type="checkbox"
                                className="toggle toggle-sm"
                                checked={form.active}
                                onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                            />
                            <span className="text-sm">Ligado (começa a enviar assim que salvar)</span>
                        </label>

                        <div className="flex justify-end gap-2 pt-2 border-t border-base-300">
                            <button
                                className="btn btn-sm btn-ghost"
                                onClick={() => {
                                    setShowForm(false)
                                    setForm(EMPTY_FORM)
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={handleCreate}
                                disabled={saving}
                            >
                                {saving ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : null}
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="card bg-base-100 border border-base-300 mb-6">
                <div className="card-body gap-3">
                    <h2 className="font-semibold">Eventos mapeados</h2>
                    {loading && mappings.length === 0 ? (
                        <div className="py-8 flex justify-center">
                            <span className="loading loading-spinner" />
                        </div>
                    ) : mappings.length === 0 ? (
                        <p className="text-sm text-base-content/60">
                            Nenhum evento mapeado. Enquanto não houver mapeamento, nada é enviado às
                            plataformas.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Quando</th>
                                        <th>Avisa</th>
                                        <th>Evento</th>
                                        <th>Valor</th>
                                        <th>Situação</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {mappings.map(m => (
                                        <tr key={m.id}>
                                            <td>
                                                <div className="font-medium">
                                                    {MARKETING_EVENT_TRIGGER_LABELS[m.trigger]}
                                                </div>
                                                {m.trigger === 'STAGE_ENTERED' && (
                                                    <div className="text-xs text-base-content/60">
                                                        {m.pipelineId
                                                            ? pipelineName(m.pipelineId)
                                                            : 'Qualquer funil'}
                                                        {' · '}
                                                        {m.stageId
                                                            ? stageName(m.pipelineId, m.stageId)
                                                            : 'Qualquer etapa'}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-sm">
                                                {MARKETING_EVENT_DESTINATION_LABELS[m.destination]}
                                            </td>
                                            <td className="font-mono text-sm">{m.eventName}</td>
                                            <td className="text-xs">
                                                {MARKETING_EVENT_VALUE_SOURCE_LABELS[m.valueSource]}
                                            </td>
                                            <td>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="toggle toggle-sm"
                                                        checked={m.active}
                                                        disabled={!canUpdate}
                                                        onChange={() => handleToggle(m)}
                                                    />
                                                    <span className="text-xs">
                                                        {m.active ? 'Ligado' : 'Desligado'}
                                                    </span>
                                                </label>
                                            </td>
                                            <td className="text-right">
                                                {canDelete && (
                                                    <button
                                                        className="btn btn-ghost btn-xs text-error"
                                                        onClick={() => setToDelete(m)}
                                                        title="Excluir"
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="card bg-base-100 border border-base-300">
                <div className="card-body gap-3">
                    <h2 className="font-semibold">Últimos envios</h2>
                    <p className="text-xs text-base-content/60">
                        "Não enviado" quase sempre quer dizer que o lead não tem identificador de
                        anúncio: a plataforma não teria com quem casar o evento, e mandar assim não
                        ajuda a otimização.
                    </p>
                    {dispatches.length === 0 ? (
                        <p className="text-sm text-base-content/60">Nenhum envio ainda.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Quando</th>
                                        <th>Evento</th>
                                        <th>Destino</th>
                                        <th>Referência</th>
                                        <th>Situação</th>
                                        <th>Motivo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dispatches.map(d => (
                                        <tr key={d.id}>
                                            <td className="text-xs whitespace-nowrap">
                                                {formatDateTime(d.occurredAt)}
                                            </td>
                                            <td className="font-mono text-sm">{d.eventName}</td>
                                            <td className="text-xs">
                                                {MARKETING_EVENT_DESTINATION_LABELS[d.destination]}
                                            </td>
                                            <td className="text-xs">
                                                {d.orderId
                                                    ? `Procedimento #${d.orderId}`
                                                    : d.dealId
                                                      ? `Oportunidade #${d.dealId}`
                                                      : d.leadId
                                                        ? `Lead #${d.leadId}`
                                                        : '—'}
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-sm ${MARKETING_EVENT_STATUS_COLORS[d.status]}`}
                                                >
                                                    {MARKETING_EVENT_STATUS_LABELS[d.status]}
                                                </span>
                                            </td>
                                            <td className="text-xs max-w-xs break-words">
                                                {d.reason ?? '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={!!toDelete}
                onClose={() => setToDelete(null)}
                onConfirm={handleDelete}
                loading={deleting}
                title="Excluir este mapeamento?"
                message="O evento deixa de ser enviado. Os envios já feitos continuam no histórico."
                confirmButtonText="Excluir"
            />
        </div>
    )
}
